import React, { useState } from 'react';
import { FileText, Check, X, ExternalLink, GraduationCap, Database, Scale, Gift } from 'lucide-react';

const TermsOfServiceModal = ({ isOpen, onAccept, onDecline }) => {
    const [isAgeConfirmed, setIsAgeConfirmed] = useState(false);

    // Backend API URL
    const API_URL = process.env.REACT_APP_API_URL;

    const handleAccept = () => {
        if (isAgeConfirmed) {
            onAccept();
        }
    };

    const openFullTerms = () => {
        window.open(`${API_URL}/terms`, '_blank');
    };

    const openPrivacyPolicy = () => {
        window.open(`${API_URL}/privacy`, '_blank');
    };

    if (!isOpen) return null;

    const keyPoints = [
        {
            icon: GraduationCap,
            title: "Educational Use Only",
            description: "This is a study aid, not medical advice or official certification. Always verify with official sources."
        },
        {
            icon: Database,
            title: "Data We Collect",
            description: "Your email, quiz progress, and basic analytics to improve the service."
        },
        {
            icon: Scale,
            title: "No Warranties",
            description: "Service is provided \"as is\" without guarantees of accuracy or availability."
        },
        {
            icon: Gift,
            title: "Free Service",
            description: "This is a free educational tool with limited liability."
        }
    ];

    return (
        <>
            <style>{`
                @keyframes modal-fade-in {
                    0% { opacity: 0; }
                    100% { opacity: 1; }
                }
                @keyframes modal-slide-up {
                    0% { transform: translateY(20px); opacity: 0; }
                    100% { transform: translateY(0); opacity: 1; }
                }
                .modal-backdrop {
                    animation: modal-fade-in 0.3s ease-out;
                }
                .modal-content {
                    animation: modal-slide-up 0.4s ease-out;
                }
            `}</style>

            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 modal-backdrop">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg flex flex-col modal-content">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 rounded-t-3xl">
                        <div className="flex items-center gap-3">
                            <FileText className="w-8 h-8" />
                            <div>
                                <h1 className="text-2xl font-bold">Terms of Service</h1>
                                <p className="text-blue-100 text-sm">Please review before continuing</p>
                            </div>
                        </div>
                    </div>

                    {/* Key Points */}
                    <div className="p-6 space-y-4">
                        <p className="text-sm text-gray-600 font-medium">Key Points:</p>

                        <div className="space-y-3">
                            {keyPoints.map((point, index) => (
                                <div key={index} className="flex gap-3 p-3 bg-gray-50 rounded-xl">
                                    <div className="flex-shrink-0">
                                        <point.icon className="w-5 h-5 text-blue-600 mt-0.5" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-800 text-sm">{point.title}</p>
                                        <p className="text-gray-600 text-sm">{point.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Links to full documents */}
                        <div className="flex flex-col gap-2 pt-2">
                            <button
                                onClick={openFullTerms}
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                View Full Terms of Service
                            </button>
                            <button
                                onClick={openPrivacyPolicy}
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                            >
                                <ExternalLink className="w-4 h-4" />
                                View Privacy Policy
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 p-6 bg-gray-50 rounded-b-3xl">
                        {/* Age Confirmation Checkbox */}
                        <div className="mb-4">
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <div className="relative flex-shrink-0 mt-0.5">
                                    <input
                                        type="checkbox"
                                        checked={isAgeConfirmed}
                                        onChange={(e) => setIsAgeConfirmed(e.target.checked)}
                                        className="w-5 h-5 rounded border-2 border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                    />
                                </div>
                                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                                    <strong>I am 18 years or older</strong> and I agree to the Terms of Service and Privacy Policy.
                                </span>
                            </label>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={onDecline}
                                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all"
                            >
                                <X className="w-5 h-5" />
                                Decline
                            </button>
                            <button
                                onClick={handleAccept}
                                disabled={!isAgeConfirmed}
                                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all ${
                                    isAgeConfirmed
                                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:shadow-lg hover:scale-105'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                <Check className="w-5 h-5" />
                                I Accept
                            </button>
                        </div>

                        {!isAgeConfirmed && (
                            <p className="text-xs text-gray-500 text-center mt-3">
                                Please confirm you are 18 years or older to continue
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default TermsOfServiceModal;
